/**
 * ReportsChartHook
 *
 * One AG Charts hook for every monitoring report that draws one. Like
 * `ReportsAgGridHook`, nothing here is report-specific: the server sends a
 * chart payload already pivoted into AG Charts' shape by `ReportingCore.Chart`,
 * so this hook knows nothing about currencies, merchants or dates.
 *
 * Dataset attribute:
 *   data-chart  JSON {type, data, series, x_key, x_name, y_name}
 *
 * Pivoting on the server rather than here is deliberate. AG Charts wants one
 * object per x-value with a key per series; Elixir naturally produces a list of
 * series each holding points. Doing the transform in `ReportingCore.Chart`
 * keeps that awkwardness in one tested place instead of in every chart hook.
 *
 * The chart is rebuilt rather than patched on update, matching
 * `DashboardChartHook`: a filter change can alter the row count and the series
 * shape at once, which AG Charts does not reconcile cleanly. The same rebuild
 * is what a theme flip rides on — canvas colours are baked in at construction.
 */

import { chartTheme, watchTheme, stopThemeWatch } from "../base/chartTheme";

// Series colours. Currency codes are the common series key across these
// reports, so AED keeps one colour on every chart that splits by currency —
// the same discipline DashboardChartHook applies to channels. Anything not
// listed falls through to the positional palette.
const SERIES_COLOURS = {
  AED: "#0284C7",
  USD: "#16A34A",
  INR: "#D97706",
  NZD: "#7C3AED",
  UNKNOWN: "#9CA3AF",
};

const PALETTE = [
  "#0284C7", "#7C3AED", "#DB2777", "#16A34A", "#EA580C",
  "#0D9488", "#D97706", "#DC2626", "#6366F1", "#65A30D",
];

const ReportsChartHook = {
  mounted() {
    this.render();
    watchTheme(this, this.render);
  },

  updated() {
    this.render();
  },

  destroyed() {
    stopThemeWatch(this);
    this.teardown();
  },

  teardown() {
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (_e) {
        // A chart whose container was already removed throws on destroy; the
        // node is going away regardless.
      }
      this.chart = null;
    }
  },

  render() {
    this.teardown();

    if (!window.agCharts) {
      console.error("ReportsChartHook: agCharts not found on window");
      return;
    }

    let spec;
    try {
      spec = JSON.parse(this.el.dataset.chart || "null");
    } catch (e) {
      console.warn("ReportsChartHook: failed to parse chart payload", e);
      return;
    }

    if (!spec || !Array.isArray(spec.data) || spec.data.length === 0) {
      this.el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;
        height:100%;color:#9CA3AF;font-size:13px;">Nothing to chart for these filters.</div>`;
      return;
    }

    try {
      this.chart = window.agCharts.AgCharts.create({
        container: this.el,
        data: spec.data,
        series: this.series(spec),
        axes: this.axes(spec),
        legend: { enabled: (spec.series || []).length > 1, position: "bottom" },
        ...chartTheme(),
      });
    } catch (e) {
      console.error("ReportsChartHook: failed to create chart", e);
    }
  },

  series(spec) {
    const type = spec.type === "bar" ? "bar" : spec.type === "pie" ? "pie" : "line";

    if (type === "pie") {
      const key = (spec.series[0] || {}).key;
      return [
        {
          type: "pie",
          angleKey: key,
          calloutLabelKey: spec.x_key,
          fills: PALETTE,
        },
      ];
    }

    return spec.series.map((s, index) => {
      const colour = SERIES_COLOURS[s.name] || PALETTE[index % PALETTE.length];

      return {
        type,
        xKey: spec.x_key,
        yKey: s.key,
        yName: s.name,
        ...(type === "bar"
          ? { fill: colour, stacked: spec.series.length > 1 }
          : { stroke: colour, marker: { fill: colour, stroke: colour, size: 5 } }),
      };
    });
  },

  axes(spec) {
    if (spec.type === "pie") return undefined;

    return [
      { type: "category", position: "bottom", title: { text: spec.x_name || "" } },
      { type: "number", position: "left", title: { text: spec.y_name || "" } },
    ];
  },
};

export default ReportsChartHook;
